import {
    Viewer,
    CzmlDataSource,
    Cartesian3,
    PolylineGraphics,
    PositionPropertyArray,
    ReferenceProperty,
    Entity,
    TimeIntervalCollection,
    TimeInterval,
    JulianDate,
    CustomDataSource,
    ArcType,
} from 'cesium';

import { useAppDispatch } from 'hooks';
import { getScheduleSummeryList } from 'state/schedules';
import { getGroundStationCZML } from 'state/groundstations';
import { getOrbitCZML, getOrbit } from 'state/orbits';
import {
    AntennaManager,
    GroundStationManager,
    OrbitManager,
    ScheduleManager,
} from 'models';
import { MapControl } from 'controllers';

export default class VisualiseGroundStationActivityController
    implements MapControl
{
    view?: Viewer;

    groundStationDatasources: Record<number, CzmlDataSource> = {};

    orbitDatasources: Record<number, CzmlDataSource> = {};

    schedulesCommsDatasource: CustomDataSource;

    constructor(
        public antennas: AntennaManager.Antenna[],
        public dispatch: ReturnType<typeof useAppDispatch>,
        public zoomTo?: number,
        public historicTimeMillis = 6 * 60 * 60 * 1000, // Get schedules from 6 hours ago
    ) {
        this.schedulesCommsDatasource = new CustomDataSource('Scheduled Comms');
    }

    async reload() {
        if (!this.view) return;

        console.log('Reloading Datasources');

        // Remove old stuff
        this.clear();

        // For algorithm see
        // https://bitbucket.org/ournova/nova-ign/src/master/ign_frontend/src/app/components/CesiumViewer/index.tsx

        const groundstations: Record<
            number,
            GroundStationManager.GroundStation
        > = {};

        // For each antenna
        //      get ground station ids
        //      get schedules-min for the next X days and add to full list
        const schedulePromises: Promise<ScheduleManager.ScheduleSummary[]>[] =
            [];
        this.antennas.forEach((antenna) => {
            groundstations[antenna.groundstation.id] = antenna.groundstation;

            schedulePromises.push(
                this.dispatch(
                    getScheduleSummeryList({
                        limit: 500,
                        after_start_time: new Date(
                            Date.now() - this.historicTimeMillis,
                        ).toISOString(),
                        antenna: antenna.id,
                        ordering: 'start_time',
                    }),
                ),
            );
        });

        const scheduleSummeryPartList = await Promise.all(schedulePromises);

        // Join schedule summeries into 1 list
        const scheduleSummeryList = scheduleSummeryPartList.reduce(
            (list, otherList) => {
                list.push(...otherList);
                return list;
            },
        );

        // for each ground station id
        //      get each ground station details
        //      get ground station czml
        //      add ground station czml to map
        const gsCZMLPromises: Promise<any>[] = [];
        Object.keys(groundstations).forEach((id) => {
            gsCZMLPromises.push(
                new Promise((resolve) => {
                    this.dispatch(getGroundStationCZML({ id })).then((czml) => {
                        const ds = new CzmlDataSource();

                        this.view?.dataSources?.add(ds);

                        this.groundStationDatasources[Number.parseInt(id, 10)] =
                            ds;

                        ds.load(czml).then(() => {
                            resolve(czml);
                        });
                    });
                }),
            );
        });

        await Promise.all(gsCZMLPromises);

        // Get list of relevant orbits from schedule summery
        const orbits: Record<number, OrbitManager.Orbit | null> = {};

        scheduleSummeryList.forEach((schedule) => {
            if (schedule.orbit) orbits[schedule.orbit] = null;
        });

        // for each orbit id
        //      get each orbit details
        //      get each orbit czml
        //      add orbit czml to map

        const orbitPromises: Promise<any>[] = [];

        Object.keys(orbits).forEach((id_str) => {
            const id = Number.parseInt(id_str, 10);

            orbitPromises.push(
                this.dispatch(getOrbit({ id })).then((orbit) => {
                    orbits[id] = orbit;
                }),
            );

            orbitPromises.push(
                new Promise((resolve) => {
                    this.dispatch(getOrbitCZML({ id })).then((czml) => {
                        const ds = new CzmlDataSource();

                        this.view?.dataSources.add(ds);

                        this.orbitDatasources[id] = ds;

                        ds.load(czml).then(() => {
                            resolve(czml);
                        });
                    });
                }),
            );
        });

        await Promise.all(orbitPromises);

        // for each schedule in the full list
        //      build a polyline object between the schedules ground station and satellite
        //         between the relevant times
        //      add this to the map
        let earliestTime = new Date(Date.now() - 1000);
        let latestTime = new Date(Date.now() + 1000);

        scheduleSummeryList.forEach((schedule) => {
            // Look for earliest time
            if (schedule.start_time < earliestTime)
                earliestTime = schedule.start_time;
            // Look for latest time
            if (schedule.stop_time > latestTime)
                latestTime = schedule.stop_time;

            const antenna = this.antennas.find(
                (a) => a.id === schedule.antenna,
            );

            if (!antenna || schedule.orbit == null) return;

            const { groundstation } = antenna;

            const orbit = orbits[schedule.orbit];

            if (!orbit) return;

            const { satellite } = orbit;

            this.schedulesCommsDatasource.entities.add(
                new Entity({
                    availability: new TimeIntervalCollection([
                        new TimeInterval({
                            start: JulianDate.fromDate(schedule.start_time),
                            stop: JulianDate.fromDate(schedule.stop_time),
                            data: schedule,
                        }),
                    ]),
                    id: `${schedule.id}: ${groundstation.name} - ${
                        satellite?.name ?? schedule.satelliteName
                    }`,
                    description: `Scheduled Communications with status "${
                        schedule.status
                    }" and duration of ${Math.round(
                        schedule.duration,
                    )}s, between the Ground Station ${
                        groundstation.name
                    } and the Satellite ${
                        satellite?.name ?? schedule.satelliteName
                    }.`,
                    polyline: new PolylineGraphics({
                        positions: new PositionPropertyArray([
                            new ReferenceProperty(
                                this.groundStationDatasources[
                                    groundstation.id
                                ].entities,
                                `GroundStation/${groundstation.name}`,
                                ['position'],
                            ),
                            new ReferenceProperty(
                                this.orbitDatasources[orbit.id].entities,
                                `Satellite/${
                                    satellite?.name ?? schedule.satelliteName
                                }`,
                                ['position'],
                            ),
                        ]),
                        arcType: ArcType.NONE,
                    }),
                }),
            );
        });

        if (this.zoomTo != null && this.antennas[this.zoomTo])
            this.view.camera.flyTo({
                destination: Cartesian3.fromDegrees(
                    this.antennas[this.zoomTo].lon,
                    this.antennas[this.zoomTo].lat,
                    this.view.camera.positionCartographic.height,
                ),
            });

        // Offset start/end by half the buffer time
        earliestTime = new Date(
            earliestTime.getTime() - this.historicTimeMillis / 2,
        );
        latestTime = new Date(
            latestTime.getTime() - this.historicTimeMillis / 2,
        );

        this.view.clock.startTime = JulianDate.fromDate(earliestTime);
        this.view.clock.stopTime = JulianDate.fromDate(latestTime);
        this.view.clock.currentTime = JulianDate.fromDate(new Date());
        this.view.clock.multiplier = 1;

        // this.view.timeline.updateFromClock();
        this.view.timeline.zoomTo(
            this.view.clock.startTime,
            this.view.clock.stopTime,
        );
    }

    updateAntennas(antennas: AntennaManager.Antenna[], zoomTo?: number) {
        this.antennas = antennas;
        this.zoomTo = zoomTo;

        this.reload();
    }

    apply(view: Viewer) {
        this.view = view;

        console.log('Applying Datasource');
        this.view?.dataSources?.add(this.schedulesCommsDatasource);

        this.reload();
    }

    clear() {
        console.log('Clearing Datasources');
        Object.keys(this.groundStationDatasources).forEach((id_str) => {
            const id = Number.parseInt(id_str, 10);
            this.view?.dataSources?.remove(this.groundStationDatasources[id]);
            delete this.groundStationDatasources[id];
        });

        Object.keys(this.orbitDatasources).forEach((id_str) => {
            const id = Number.parseInt(id_str, 10);
            this.view?.dataSources?.remove(this.orbitDatasources[id]);
            delete this.orbitDatasources[id];
        });

        this.schedulesCommsDatasource.entities.removeAll();
    }

    destroy() {
        this.clear();

        console.log('Destroying Datasource');
        this.view?.dataSources.remove(this.schedulesCommsDatasource);
    }
}
